// src/pages/About.js
import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import {
  ArrowRightIcon,
  HeartIcon,
  SparklesIcon,
  UserGroupIcon,
  BookOpenIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const About = () => {
  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="relative overflow-hidden bg-gray-900">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1524117074681-31bd4de22ad3?ixlib=rb-4.0.3"
            alt="People gathered in conversation"
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        <div className="relative px-6 py-28 sm:py-36 lg:px-12">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-secondary font-semibold text-sm uppercase tracking-widest mb-4">
              About Together On Mission
            </p>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl leading-tight">
              Called to Make Disciples.<br className="hidden sm:block" />
              <span className="text-secondary">Equipped to Actually Do It.</span>
            </h1>
            <p className="mt-6 text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Every believer has the same commission and the same Spirit. Together On Mission
              gives you a simple system to be faithful with both — one person, one conversation,
              one week at a time.
            </p>
          </div>
        </div>
      </section>

      {/* The Story */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?ixlib=rb-4.0.3"
                alt="People connecting in community"
                className="aspect-[3/2] w-full rounded-2xl object-cover shadow-xl"
              />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10"></div>
            </div>
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
                Here's What We Noticed
              </h2>
              <div className="space-y-5 text-lg text-gray-600 leading-relaxed">
                <p>
                  Churches are full of people who <em>want</em> to make disciples. They've heard the
                  sermons. They feel the pull. They might even have someone in mind.
                </p>
                <p>
                  But a week goes by. Then a month. The conversation never happens —
                  not because they don't care, but because <strong className="text-gray-900">life
                  doesn't have a "disciple someone" button.</strong>
                </p>
                <p>
                  There's no reminder. No idea what to say. No sense of whether it's
                  actually working. And many believers assume it's the pastor's job —
                  even though the Great Commission was given to <em>every</em> follower of Jesus.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Shift */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
            What If It Were Simple?
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-16">
            What if a willing mentor could open an app, see who to reach out to today,
            get a Bible verse that fits that person's journey, and send a meaningful message
            in under two minutes? What if that happened every week — automatically?
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {shifts.map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 mx-auto mb-5">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
                This Is What TOM Does
              </h2>
              <div className="space-y-6">
                {howItWorks.map((step, i) => (
                  <div key={step.title} className="flex gap-5">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-secondary text-white flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1516321497487-e288fb19713f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1770&q=80"
                alt="Person using phone thoughtfully"
                className="aspect-[3/2] w-full rounded-2xl object-cover shadow-xl"
              />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Built for Anyone Who's Willing
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              You don't need a title, a degree, or a curriculum. You just need to care about
              someone enough to walk with them.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {audiences.map((a) => (
              <div key={a.title} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-4 mb-5">
                  <div className={`h-12 w-12 rounded-xl ${a.bgColor} flex items-center justify-center`}>
                    <a.icon className={`h-6 w-6 ${a.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{a.title}</h3>
                    <p className="text-sm text-gray-400">{a.subtitle}</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">{a.description}</p>
                <ul className="space-y-2">
                  {a.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-gray-500">
                      <span className="text-secondary mt-0.5">&#10003;</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Vision */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold sm:text-4xl mb-8">
            Imagine What Happens When Every Willing Heart Has a System
          </h2>
          <div className="grid sm:grid-cols-3 gap-10 mb-14">
            {visionPoints.map((v) => (
              <div key={v.stat}>
                <div className="text-4xl font-extrabold text-secondary mb-2">{v.stat}</div>
                <p className="text-gray-400 text-sm leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
          <blockquote className="text-lg md:text-xl text-gray-300 leading-relaxed italic max-w-3xl mx-auto">
            "The harvest is plentiful, but the workers are few. <span className="text-white not-italic font-semibold">What
            if the workers just needed better tools?</span>"
          </blockquote>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">
            Start Today. It's Free.
          </h2>
          <p className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto">
            Sign up as an individual and start discipling someone this week.
            If you're a church leader, bring your whole team — the platform
            includes your first 5 disciplemaker seats.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center bg-primary text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition duration-300"
            >
              Sign Up Free
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
            <Link
              to="/for-churches"
              className="inline-flex items-center justify-center bg-secondary text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition duration-300"
            >
              See the Church Plan
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* --- Data --- */

const shifts = [
  {
    title: 'A Nudge, Not Guilt',
    description: 'Personalized reminders based on each relationship — not a mass email, not a calendar event. A gentle system that keeps you consistent.',
    icon: BellAlertIcon,
  },
  {
    title: 'Words That Fit',
    description: "Smart Bible verse suggestions based on where your person is spiritually. You don't have to figure out what to say — just review, personalize, and send.",
    icon: SparklesIcon,
  },
  {
    title: "Proof It's Working",
    description: 'Every prayer, every message, every conversation tracked. For you it builds confidence. For leaders it builds visibility.',
    icon: ChatBubbleLeftRightIcon,
  },
];

const howItWorks = [
  {
    title: 'Add the people you care about',
    description: "Enter a name, a phone number, a few notes about where they are spiritually. That's your starting point.",
  },
  {
    title: "The app remembers so you don't have to",
    description: "Set a reminder cycle — every 3 days, every week, every two weeks. TOM nudges you when it's time to reach out.",
  },
  {
    title: 'Get a personalized message ready to send',
    description: 'A Bible verse picked for their journey, a reflection question, your own words. Review it, tweak it, send it via text or email.',
  },
  {
    title: 'Pray with purpose',
    description: "Daily prayer suggestions based on each person's stage and needs. One tap to pray, one tap for a new suggestion.",
  },
  {
    title: 'Watch the relationship grow',
    description: 'Notes, message history, spiritual milestones — all in one place. Over time, the app learns what resonates and gets smarter.',
  },
];

const audiences = [
  {
    title: 'Individual Disciplemakers',
    subtitle: 'Always free',
    description: "You have someone in your life who needs encouragement, accountability, or a deeper walk with God. You don't need a program — you need a system that keeps you showing up.",
    bullets: [
      'Add unlimited disciples',
      'Smart verse suggestions tailored to each person',
      'Reminder cycles that keep you consistent',
      'Prayer guidance and conversation notes',
      'Content tracks to walk through together',
      'Read Together for parents and kids',
    ],
    icon: HeartIcon,
    bgColor: 'bg-primary/10',
    iconColor: 'text-primary',
  },
  {
    title: 'Churches & Ministries',
    subtitle: 'Platform + seat packs',
    description: "You want discipleship to be the culture of your church, not just a program. You need visibility, content distribution, and a way to equip your people at scale.",
    bullets: [
      'Organization dashboard with real-time activity',
      'Push your own content through disciplemakers',
      'Announcements and community story feed',
      'Invite members by email or org code',
      'Seat management — platform includes first 5',
      'Great Commission impact stats',
    ],
    icon: UserGroupIcon,
    bgColor: 'bg-secondary/10',
    iconColor: 'text-secondary',
  },
];

const visionPoints = [
  {
    stat: '1 mentor',
    description: "One person who shows up consistently can change the trajectory of someone's entire spiritual life.",
  },
  {
    stat: '1 message',
    description: 'A Bible verse sent at the right moment can open a conversation that lasts for years.',
  },
  {
    stat: '1 church',
    description: 'When a church full of disciplemakers is equipped and connected, a city starts to change.',
  },
];

export default About;
